import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as api from '../api/api';

// Cache global para las traducciones
let translationsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Hook para obtener y usar traducciones dinamicas de claves de prompts
 *
 * @returns {Object} - { translations, getLabel, isLoading, error, refreshTranslations }
 */
export function usePromptTranslations() {
  const { i18n } = useTranslation();
  const [translations, setTranslations] = useState(translationsCache);
  const [isLoading, setIsLoading] = useState(!translationsCache);
  const [error, setError] = useState(null);

  const currentLocale = i18n.language || 'es';

  const fetchTranslations = useCallback(async (forceRefresh = false) => {
    const now = Date.now();

    // Usar cache si es valido y no se fuerza refresh
    if (!forceRefresh && translationsCache && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
      setTranslations(translationsCache);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await api.getTranslations();
      translationsCache = data;
      cacheTimestamp = now;
      setTranslations(data);
    } catch (err) {
      console.error('Error al cargar traducciones:', err);
      setError(err);
      // Si hay cache viejo, usarlo como fallback
      if (translationsCache) {
        setTranslations(translationsCache);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTranslations();
  }, [fetchTranslations]);

  /**
   * Obtiene la traduccion para una clave especifica
   *
   * @param {string} key - La clave a traducir (ej: "Identificacion_tipo_llamada")
   * @param {string} category - La categoria (Summary, VOCSummary, QualityEvaluation, CustomerExperience)
   * @param {string} fallback - Valor por defecto si no se encuentra traduccion
   * @returns {string} - La traduccion o el fallback
   */
  const getLabel = useCallback((key, category, fallback = null) => {
    if (!translations || !key) {
      return fallback || key;
    }

    // Intentar obtener traduccion del locale actual
    const localeTranslations = translations[currentLocale];
    if (localeTranslations && localeTranslations[category]) {
      const translation = localeTranslations[category][key];
      if (translation) {
        return translation;
      }
    }

    // Fallback a espanol si el locale actual no tiene la traduccion
    if (currentLocale !== 'es' && translations['es'] && translations['es'][category]) {
      const esTranslation = translations['es'][category][key];
      if (esTranslation) {
        return esTranslation;
      }
    }

    // Si no hay traduccion, devolver el fallback (ej: clave original corta) para que sea visible
    return fallback || key;
  }, [translations, currentLocale]);

  /**
   * Obtiene todas las traducciones de una categoria
   *
   * @param {string} category - La categoria
   * @returns {Object} - Objeto con todas las traducciones de esa categoria
   */
  const getCategoryTranslations = useCallback((category) => {
    if (!translations) {
      return {};
    }

    const localeTranslations = translations[currentLocale];
    if (localeTranslations && localeTranslations[category]) {
      return localeTranslations[category];
    }

    // Fallback a espanol
    if (translations['es'] && translations['es'][category]) {
      return translations['es'][category];
    }

    return {};
  }, [translations, currentLocale]);

  /**
   * Traduce un objeto completo de resultados de prompt (soporta campos anidados)
   *
   * @param {Object} data - Objeto con claves a traducir
   * @param {string} category - La categoria
   * @param {string} parentKey - Clave padre para campos anidados (uso interno)
   * @returns {Array} - Array de { label, value, originalKey, children? } con las claves traducidas
   */
  const translatePromptResults = useCallback((data, category, parentKey = '') => {
    if (!data || typeof data !== 'object') {
      return [];
    }

    return Object.entries(data)
      .filter(([key]) => key !== 'insightsPrompt') // Excluir campos internos
      .map(([key, value]) => {
        // Construir la clave compuesta para buscar traduccion (ej: "Experiencia_del_cliente.Resumen_general")
        const fullKey = parentKey ? `${parentKey}.${key}` : key;

        // Si el valor es un objeto (no array), procesar recursivamente
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          return {
            label: getLabel(fullKey, category, key),
            originalKey: key,
            fullKey: fullKey,
            isNested: true,
            children: translatePromptResults(value, category, fullKey)
          };
        }

        // Valor simple o array
        return {
          label: getLabel(fullKey, category, key),
          value: Array.isArray(value) ? value.join(', ') : value,
          originalKey: key,
          fullKey: fullKey,
          isNested: false
        };
      });
  }, [getLabel]);

  const refreshTranslations = useCallback(() => {
    return fetchTranslations(true);
  }, [fetchTranslations]);

  return {
    translations,
    getLabel,
    getCategoryTranslations,
    translatePromptResults,
    isLoading,
    error,
    refreshTranslations,
    currentLocale
  };
}

export default usePromptTranslations;
